
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Undo, RefreshCw, ArrowDown, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';
import { listTeacherBackups, restoreFromLocalTeacherBackup } from '@/services/backup/teacherBackup';
import { motion } from 'framer-motion';

interface TeacherBackupInfo {
  id: string;
  timestamp: string;
  size: number;
  description?: string;
}

interface TeacherBackupListProps {
  onRestoreComplete?: () => void;
}

const TeacherBackupList: React.FC<TeacherBackupListProps> = ({ onRestoreComplete }) => {
  const [backups, setBackups] = useState<TeacherBackupInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRestoring, setIsRestoring] = useState(false);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [restoringId, setRestoringId] = useState<string | null>(null);
  
  useEffect(() => {
    loadBackups();
  }, []);
  
  const loadBackups = () => {
    try {
      setIsLoading(true);
      const backupsList = listTeacherBackups();
      setBackups(backupsList);
    } catch (error) {
      console.error('Error loading teacher backups:', error);
      toast.error('Failed to load teacher backup history');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRestore = async (backupId: string) => {
    if (isRestoring) return;
    
    setIsRestoring(true);
    setRestoringId(backupId);
    
    try {
      const result = await restoreFromLocalTeacherBackup(backupId);
      if (result) {
        toast.success('Teacher backup restored successfully');
        if (onRestoreComplete) {
          onRestoreComplete();
        }
      }
    } catch (error) {
      console.error('Failed to restore teacher backup:', error);
      toast.error('Failed to restore teacher backup');
    } finally {
      setIsRestoring(false);
      setRestoringId(null);
    }
  };
  
  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };
  
  const sortedBackups = [...backups].sort((a, b) => {
    const dateA = new Date(a.timestamp).getTime();
    const dateB = new Date(b.timestamp).getTime();
    return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
  });
  
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (backups.length === 0) {
    return (
      <motion.div 
        className="text-center py-6 text-muted-foreground"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        No backup history found
      </motion.div>
    );
  }
  
  return (
    <motion.div
      className="space-y-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={toggleSortDirection}>
          Sort {sortDirection === 'asc' ? 'Newest First' : 'Oldest First'}
          {sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />}
        </Button>
      </div>
      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Size</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedBackups.map((backup) => (
              <TableRow key={backup.id}>
                <TableCell>
                  {new Date(backup.timestamp).toLocaleDateString()}{' '}
                  {new Date(backup.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </TableCell>
                <TableCell>{backup.description || 'Teacher backup'}</TableCell>
                <TableCell>{formatSize(backup.size)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRestore(backup.id)}
                    disabled={isRestoring}
                  >
                    {isRestoring && restoringId === backup.id ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Undo className="h-4 w-4 mr-1" />
                    )}
                    Restore
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </motion.div>
  );
};

export default TeacherBackupList;
